import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { RootState } from '../../../state/state';
import { FeeService } from '../fee.service';
import {
  getAllBagsAndAncillaryFees,
  getAllBagsAndAncillaryFeesComplete,
  loadFeeResults,
  loadFeeResultsComplete,
  getFeesWorkflowSdcFeeResults,
  getFeesWorkflowSdcFeeResultsComplete,
  getDynamicFees,
  getDynamicFeesComplete,
  getDynamicFeesFailed,
} from './fee.actions';
import { FeeSelectors } from './fee.selectors';

@Injectable()
export class FeeEffects {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly actions$ = inject(Actions);
  private readonly service = inject(FeeService);

  loadFeeResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadFeeResults),
      withLatestFrom(this.store.select(FeeSelectors.getFeeRequest)),
      mergeMap(([action, request]) =>
        this.service.getFees(request, true).pipe(map((result) => loadFeeResultsComplete(result.success, result)))
      ),
      catchError(() => of(loadFeeResultsComplete(false, null)))
    )
  );

  getAllBagsAndAncillaryFees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getAllBagsAndAncillaryFees),
      withLatestFrom(this.store.select(FeeSelectors.getFeeRequest)),
      mergeMap(([action, request]) =>
        this.service.getAllBagsAndAncillaryFees(request).pipe(map((result) => getAllBagsAndAncillaryFeesComplete(result.success, result)))
      ),
      catchError(() => of(getAllBagsAndAncillaryFeesComplete(false, null)))
    )
  );

  getFeesWorkflowSdcFeeResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getFeesWorkflowSdcFeeResults),
      withLatestFrom(this.store.select(FeeSelectors.getFeesWorkflowSdcFeeRequest)),
      mergeMap(([action, request]) =>
        this.service.getFees(request, false).pipe(map((result) => getFeesWorkflowSdcFeeResultsComplete(result.success, result)))
      ),
      catchError(() => of(getFeesWorkflowSdcFeeResultsComplete(false, null)))
    )
  );

  getDynamicFees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDynamicFees),
      withLatestFrom(this.store.select(FeeSelectors.getDynamicFeesRequest)),
      mergeMap(([_, request]) =>
        this.service.getDynamicFees(request, true).pipe(
          map((result) => getDynamicFeesComplete({ response: result, requestPassengers: request.passengers ?? [] })),
          catchError((error) => of(getDynamicFeesFailed(error.error?.toString() ?? 'Unknown error')))
        )
      )
    )
  );
}
